import { useRef } from 'react';
import { Search } from 'lucide-react';

interface Props {
  value: string;
  placeholder?: string;
  disabled?: boolean;
  shortcutLabel?: string;

  clearOnEscape?: boolean;
  autoFocusOnClear?: boolean;

  onChange: (value: string) => void;
}

const SearchField = ({
  value,
  placeholder = 'Buscar...',
  disabled = false,
  shortcutLabel,

  clearOnEscape = true,
  autoFocusOnClear = true,

  onChange,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleEscape = () => {
    onChange('');

    if (!autoFocusOnClear) {
      return;
    }

    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  };

  return (
    <div className="w-full">
      <div
        className={`
          relative

          flex
          h-[44px]
          items-center

          overflow-hidden

          rounded-xl
          border

          shadow-1
          transition-all
          duration-200

          ${
            disabled
              ? `
                cursor-not-allowed
                border-slate-200
                bg-slate-100
              `
              : `
                border-slate-300
                bg-white

                focus-within:border-primary
                focus-within:ring-4

                focus-within:ring-primary/15
                hover:border-primary/50
                hover:shadow-2
              `
          }

          dark:border-form-strokedark
          dark:bg-boxdark-2

          dark:focus-within:border-primary
          dark:focus-within:ring-primary/20
        `}
      >
        {/* 🔹 Icon */}

        <div
          className={`
            flex
            h-full
            w-[44px]
            shrink-0

            items-center
            justify-center

            border-r

            ${
              disabled
                ? `
                  border-slate-300
                  bg-slate-200
                  text-slate-500
                `
                : `
                  border-primary
                  bg-primary
                  text-white
                `
            }

            dark:border-strokedark
          `}
        >
          <Search className="h-4 w-4" />
        </div>

        {/* 🔹 Input */}

        <input
          ref={inputRef}
          type="text"
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (clearOnEscape && e.key === 'Escape') {
              e.preventDefault();
              handleEscape();
            }
          }}
          className={`
            h-full
            w-full

            bg-transparent

            px-4
            ${shortcutLabel ? 'pr-16' : 'pr-4'}

            text-sm
            font-medium

            outline-none

            ${
              disabled
                ? `
                  cursor-not-allowed
                  text-slate-400
                  placeholder:text-slate-300
                `
                : `
                  text-slate-700
                  placeholder:text-slate-400
                `
            }

            dark:text-white
            dark:placeholder:text-bodydark2
          `}
        />

        {/* 🔹 Shortcut */}

        {shortcutLabel && !disabled && (
          <div
            className="
              absolute
              right-3

              rounded-md
              border

              border-slate-200
              bg-slate-50

              px-2
              py-1

              text-[10px]
              font-medium

              text-slate-400

              dark:border-form-strokedark
              dark:bg-boxdark
              dark:text-bodydark2
            "
          >
            {shortcutLabel}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchField;
